import { useState } from 'react';

import { cn } from '@/lib/utils';

import type { AgentDirection } from '../types';

/**
 * Platform-owned per-agent icons, bundled with the SPA (one `<code>.svg` per
 * agent in `src/assets/agents/`). Set once by us, shown to every tenant. Drop a
 * new `<code>.svg` in that folder to override an agent's art — no code change.
 * Agents without an asset fall back to the tinted initials tile below.
 */
const ICON_URLS = import.meta.glob('../../../assets/agents/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const ICON_BY_CODE: Record<string, string> = {};
for (const [path, url] of Object.entries(ICON_URLS)) {
  const match = path.match(/([^/]+)\.svg$/);
  if (match) ICON_BY_CODE[match[1].toLowerCase()] = url;
}

/** First two alphanumerics of the agent code, for the fallback tile. */
function agentInitials(code: string): string {
  return code.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase();
}

/** Soft, per-direction tint for the fallback tile. */
const DIRECTION_TINT: Record<AgentDirection, string> = {
  strategy: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-300',
  marketing: 'bg-rose-500/10 text-rose-600 dark:text-rose-300',
  sales: 'bg-amber-500/10 text-amber-600 dark:text-amber-300',
  outbound_bd: 'bg-orange-500/10 text-orange-600 dark:text-orange-300',
  talent: 'bg-violet-500/10 text-violet-600 dark:text-violet-300',
  delivery: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  engineering: 'bg-sky-500/10 text-sky-600 dark:text-sky-300',
  internal_ops: 'bg-slate-500/10 text-slate-600 dark:text-slate-300',
};

/** Square agent "image". Priority: an admin-uploaded `imageUrl` → a bundled
 *  starter icon (`<code>.svg`) → a tinted initials tile. Used across the admin
 *  catalog, roster, and detail/results pages. */
export function AgentTile({
  code,
  direction,
  imageUrl,
  className,
}: {
  code: string;
  direction: AgentDirection;
  /** Platform-uploaded icon URL from the API (overrides the bundled starter). */
  imageUrl?: string | null;
  className?: string;
}) {
  // Image sources in priority order: admin-uploaded → bundled starter. If one
  // fails to load (e.g. an unreachable media URL) we record it and fall through
  // to the next, and finally to the tinted initials tile — so no surface ever
  // shows a broken <img>. State is only touched from the onError event.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const sources = [imageUrl, ICON_BY_CODE[code.toLowerCase()]].filter(
    (s): s is string => Boolean(s),
  );
  const src = sources.find((s) => !failed.has(s));
  if (src) {
    return (
      <img
        key={src}
        src={src}
        alt=""
        onError={() => setFailed((prev) => new Set(prev).add(src))}
        className={cn('shrink-0 rounded-lg object-cover', className)}
      />
    );
  }
  return (
    <div
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg font-semibold',
        DIRECTION_TINT[direction] ?? 'bg-muted text-foreground',
        className,
      )}
    >
      {agentInitials(code)}
    </div>
  );
}
